/**
 * The base URL for API requests
 * In development, this points to our local API server
 * In production, it should be empty to use the same origin
 */
const API_BASE_URL = '';

/**
 * R2 Worker URL for assets
 * This worker handles asset storage and retrieval operations
 */

// Worker URL for asset retrieval (doesn't support uploads yet)
const ASSETS_WORKER_URL = 'https://model-viewer-assets.adrian-a87.workers.dev';

/**
 * Determine if we're in production mode (deployed site)
 * This will help us format the URLs correctly for each environment
 */
const isProd = () => {
    return window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1');
};

/**
 * Load hotspots for a model from the server
 * @param {string} modelId - The ID of the model to load hotspots for
 * @returns {Promise<Array>} - Array of hotspot data
 */
export async function loadHotspots(modelId) {
    console.log("Loading hotspots for model:", modelId);
    
    try {
        // Use path parameter in both production and development
        const url = `${API_BASE_URL}/api/hotspots/${modelId}`;
        console.log("🔍 Requesting URL:", url);
        
        const response = await fetch(url, {
            headers: {
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
            }
        });
        console.log("📊 Load response status:", response.status);
        console.log("📋 Load response headers:", Object.fromEntries([...response.headers]));
        
        const responseText = await response.text();
        // Only log the first 200 characters of the response to avoid console flooding
        const truncatedResponse = responseText.length > 200 
            ? responseText.substring(0, 200) + '...' 
            : responseText;
        console.log("📝 Load response text (truncated):", truncatedResponse);
        console.log("📝 Raw response length:", responseText.length);
        console.log("📝 First 20 characters:", responseText.substring(0, 20).split('').map(c => c.charCodeAt(0)));
        
        let hotspots = [];
        
        if (responseText && responseText.trim()) {
            try {
                // Check if response is HTML instead of JSON (indicating routing issue)
                if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
                    console.error("🚨 Received HTML instead of JSON. Routing issue detected.");
                    return [];
                }
                
                const data = responseText.trim() ? JSON.parse(responseText) : [];
                
                // Handle both formats - array of database records or direct hotspots array
                if (Array.isArray(data)) {
                    if (data.length > 0 && data[0].hasOwnProperty('model_name')) {
                        // This is array of database records, convert to expected format
                        console.log("📦 Received database record format, converting");
                        hotspots = data.map(record => {
                            console.log("📄 Converting record:", record);
                            return {
                                slot: record.slot,
                                position: record.position,
                                normal: record.normal,
                                name: record.name,
                                text: record.text,
                                color: record.color,
                                imageUrl: record.imageUrl,
                                thumbnailUrl: record.thumbnailUrl
                            };
                        });
                    } else {
                        // This is already in the expected format
                        console.log("📦 Data already in expected format");
                        hotspots = data;
                    }
                }
                
            } catch (parseError) {
                console.error("🚨 JSON parse error:", parseError);
                console.error("🚨 First 50 chars of response:", responseText.substring(0, 50));
                
                // Display error message to user
                if (isProd()) {
                    window.displayErrorMessage("Kunde inte ladda anteckningar. Kontrollera din internetanslutning.");
                } else {
                    window.displayErrorMessage("Kunde inte tolka JSON-svar från servern.");
                }
                return [];
                // Return empty array on failure
            }
            
            if (Array.isArray(hotspots)) {
                hotspots = hotspots.map(hotspot => {
                    if (hotspot.imageUrl && (hotspot.imageUrl === '' || hotspot.imageUrl === 'null')) {
                        hotspot.imageUrl = null;
                    }
                    if (hotspot.thumbnailUrl && (hotspot.thumbnailUrl === '' || hotspot.thumbnailUrl === 'null')) {
                        hotspot.thumbnailUrl = null;
                    }
                    return hotspot;
                });
            }
            
            if (Array.isArray(hotspots)) {
                console.log("Hotspots loaded successfully:", hotspots);
                return hotspots;
            } else {
                console.error("Received data is not an array");
                return [];
            }
        }
        
        return [];
    } catch (error) {
        console.error("Error loading hotspots:", error);
        window.displayErrorMessage("Kunde inte ansluta till servern. Kontrollera din internetanslutning.");
        return [];
    }
}

/**
 * Save hotspots for a model to the server
 * @param {NodeList} hotspotElements - NodeList of hotspot DOM elements
 * @param {string} modelId - The ID of the model to save hotspots for
 * @returns {Promise<boolean>} - Success status
 */
export async function saveHotspots(hotspotElements, modelId) {
    const hotspots = Array.from(hotspotElements).map(element => {
        console.log("Processing hotspot for save:", element.dataset);

        const hotspot = {
            slot: element.slot || element.getAttribute("slot"),
            position: element.dataset.position,
            normal: element.dataset.normal,
            name: element.dataset.name,
            text: element.dataset.text,
            color: element.style.backgroundColor || "rgb(59, 130, 246)",
            imageUrl: null,
            thumbnailUrl: null
        };

        
        // Make sure we don't have any undefined values - convert to null
        Object.keys(hotspot).forEach(key => {
            if (hotspot[key] === undefined) {
                hotspot[key] = null;
            }
        });

        if (element.dataset.hasOwnProperty("imageUrl") && element.dataset.imageUrl) {
            let imageUrl = element.dataset.imageUrl;
            
            // Convert API URLs to direct bucket URLs to avoid CORS issues
            // Removed direct bucket URL conversion to prevent CORS issues
            
            hotspot.imageUrl = imageUrl;
 
            if (element.dataset.hasOwnProperty("thumbnailUrl") && element.dataset.thumbnailUrl) {
                let thumbnailUrl = element.dataset.thumbnailUrl;
                hotspot.thumbnailUrl = thumbnailUrl;
            } else {
                hotspot.thumbnailUrl = imageUrl;
            }
        }

        console.log("Final hotspot data for save:", hotspot);
        return hotspot;
    });
    
    console.log("Saving hotspots for model:", modelId);
    console.log("Hotspots data:", hotspots);
    
    if (hotspots.length === 0) {
        console.log("No hotspots to save, this might be a deletion operation");
    }
    
    try {
        // Use path parameter in both production and development
        const url = `${API_BASE_URL}/api/hotspots/${modelId}`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(hotspots)
        });
        
        if (response.ok) {
            console.log("Hotspots saved successfully");
            return true;
        }
        else {
    
       console.error("Error saving hotspots:", await response.text());
            window.displayErrorMessage("Kunde inte spara anteckningar. Servern svarade med ett fel.");
            return false;
        }
    } catch (error) {
        console.error("Error saving hotspots:", error);
        window.displayErrorMessage("Kunde inte spara anteckningar. Kontrollera din internetanslutning.");
        
        return false;
    }
}

/**
 * Upload an image to the server
 * @param {File} file - The image file to upload
 * @returns {Promise<Object|null>} - Object with image URLs or null if upload failed
 */
export async function uploadImage(file) {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", `hotspot-${Date.now()}-${file.name}`);
    
    // Determine whether to use absolute or relative URL
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // Use new '/images' endpoint for hotspot images
    const uploadUrl = isProd() 
        ? `${API_BASE_URL}/images` : `${API_BASE_URL}/images`;
    
    try {
        const response = await fetch(uploadUrl, {
            method: "POST",
            body: formData
        });

        if (response.ok) {
            const data = await response.json();

            
// Extract just the filename from the path or URL returned by the upload endpoint
            // Check if using new images endpoint response format
            if (data.success && data.path) {
                // The new API returns a direct URL to the resource
                return {
                    imageUrl: data.path,
                    thumbnailUrl: data.path
                };
            }
            let filename = null;
            if (data.path) {
                const pathMatch = data.path.match(/\/([^\/]+)$/);
                if (pathMatch) {
                    filename = pathMatch[1];
                } else {
                    // If no match, just use the path as is (might bej uRt a filename)
                    filename = data.path;
                }
            }

            // Create URLs that use the worker
            const imageUrl = filename ? `${ASSETS_WORKER_URL}/${filename}` : null;

            // Handle thumbnail URL similarly
            let thumbnailFilename = null;
            if (data.thumbnailUrl) {
                const pathMatch = data.thumbnailUrl.match(/\/([^\/]+)$/);
                thumbnailFilename = pathMatch ? pathMatch[1] : data.thumbnailUrl;
            }
            const thumbnailUrl = thumbnailFilename ? `${ASSETS_WORKER_URL}/${thumbnailFilename}` : imageUrl;

            return {
                path: data.path,
                imageUrl: imageUrl || data.imageUrl,
                thumbnailUrl: thumbnailUrl || data.thumbnailUrl
            };
 
        }
        
        const responseText = await response.text();
        console.error(`Error uploading image: "${responseText}" (Status: ${response.status})`);
        window.displayErrorMessage("Kunde inte ladda upp bild. Servern svarade med ett fel.");
        return null;
    } catch (error) {
        console.error("Error uploading image:", error.message || error);
        console.log("Upload URL was:", uploadUrl);
        window.displayErrorMessage("Kunde inte ladda upp bild. Kontrollera din internetanslutning.");
        return null;
    }
}
